import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { Marca } from '../componentes/Marca'
import './admin.css'

/**
 * Back-office da plataforma: login e painel.
 *
 * Fica em host próprio (`admin.clinuma.com.br`) por causa do cookie:
 * o `__Host-adm` só vale na origem exata, então o app da clínica nunca
 * o envia — e um XSS lá não alcança nada daqui.
 *
 * O painel mostra a RELAÇÃO COMERCIAL, nunca dado de paciente. Quem
 * precisa ver prontuário pede acesso à clínica, com o consentimento
 * dela; não entra por aqui.
 */

type Eu = { nome: string; email: string }

type Clinica = {
  tenant_id: string; slug: string; nome: string; vertical: string
  status: string; criado_em: string
  assinatura: string | null; plano: string | null; plano_nome: string | null
  valor_centavos: string | null; teste_ate: string | null
  profissionais: number | null; recursos: number | null
  usuarios: number | null; pacientes: number | null
  max_profissionais: number | null; max_pacientes: number | null
  ultima_marcacao: string | null; marcacoes_30d: string
}

type Plano = {
  codigo: string; nome: string; preco_centavos: string
  max_profissionais: number | null; ativo: boolean
}

type Registro = {
  ocorrido_em: string; admin_email: string; acao: string
  motivo: string; detalhe: Record<string, unknown> | null; clinica: string | null
}

const brl = (c: string | number | null) =>
  c == null ? '—' : (Number(c) / 100).toLocaleString('pt-BR',
    { style: 'currency', currency: 'BRL' })
const data = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR') : '—'

/** Quantos dias desde a última marcação — o sinal de vida da clínica. */
function silencio(iso: string | null): { dias: number; texto: string } | null {
  if (!iso) return null
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  return { dias: d, texto: d === 0 ? 'hoje' : d === 1 ? 'ontem' : `há ${d} dias` }
}

export function Admin() {
  const [eu, setEu] = useState<Eu | null>(null)
  const [carregando, setCarregando] = useState(true)

  const verificar = useCallback(async () => {
    try {
      setEu(await api<Eu>('/v1/admin/eu'))
    } catch {
      setEu(null)
    } finally { setCarregando(false) }
  }, [])
  useEffect(() => { void verificar() }, [verificar])

  if (carregando) {
    return <div className="carregando"><Marca tamanho={32} /></div>
  }
  if (!eu) return <LoginAdmin aoEntrar={verificar} />
  return <PainelAdmin eu={eu} aoSair={() => setEu(null)} />
}

/* ══ login ════════════════════════════════════════════════════════ */

function LoginAdmin({ aoEntrar }: { aoEntrar(): void }) {
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function submeter(e: React.FormEvent) {
    e.preventDefault()
    setErro(null); setEnviando(true)
    try {
      await api('/v1/admin/login', { metodo: 'POST', corpo: { email: email.trim(), senha } })
      aoEntrar()
    } catch (err) {
      // Mensagem única: distinguir "e-mail não existe" de "senha errada"
      // aqui diria a um estranho quem é administrador da plataforma.
      setErro(err instanceof ErroApi && err.status === 429
        ? 'Muitas tentativas. Espere e tente de novo.'
        : 'E-mail ou senha não conferem.')
      setEnviando(false)
    }
  }

  return (
    <main className="adm-entrar">
      <form className="adm-entrar__caixa" onSubmit={submeter}>
        <Marca tamanho={28} />
        <div>
          <h1>Painel da plataforma</h1>
          <p className="ajuda">Acesso restrito. Todo acesso é registrado.</p>
        </div>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="campo">
          <label htmlFor="a-email">E-mail</label>
          <input id="a-email" className="entrada" type="email" required autoFocus
                 autoComplete="username"
                 value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="a-senha">Senha</label>
          <input id="a-senha" className="entrada" type="password" required
                 autoComplete="current-password"
                 value={senha} onChange={(e) => setSenha(e.target.value)} />
        </div>

        <button className="botao" type="submit" disabled={enviando}>
          {enviando ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
    </main>
  )
}

/* ══ painel ═══════════════════════════════════════════════════════ */

function PainelAdmin({ eu, aoSair }: { eu: Eu; aoSair(): void }) {
  const [aba, setAba] = useState<'clinicas' | 'trilha'>('clinicas')
  const [clinicas, setClinicas] = useState<Clinica[]>([])
  const [planos, setPlanos] = useState<Plano[]>([])
  const [trilha, setTrilha] = useState<Registro[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [editando, setEditando] = useState<Clinica | null>(null)
  const [busca, setBusca] = useState('')

  const carregar = useCallback(async () => {
    try {
      const [c, p, t] = await Promise.all([
        api<{ itens: Clinica[] }>('/v1/admin/clinicas'),
        api<{ itens: Plano[] }>('/v1/admin/planos'),
        api<{ itens: Registro[] }>('/v1/admin/auditoria'),
      ])
      setClinicas(c.itens); setPlanos(p.itens); setTrilha(t.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    }
  }, [])
  useEffect(() => { void carregar() }, [carregar])

  async function sair() {
    await api('/v1/admin/logout', { metodo: 'POST' }).catch(() => {})
    aoSair()
  }

  const filtradas = clinicas.filter((c) =>
    !busca.trim() ||
    c.nome.toLowerCase().includes(busca.toLowerCase()) ||
    c.slug.includes(busca.toLowerCase()))

  const ativas = clinicas.filter((c) => c.assinatura === 'ATIVA').length
  const teste = clinicas.filter((c) => c.assinatura === 'TESTE').length
  const mrr = clinicas
    .filter((c) => c.assinatura === 'ATIVA')
    .reduce((s, c) => s + Number(c.valor_centavos ?? 0), 0)

  return (
    <div className="adm">
      <header className="adm__barra">
        <Marca tamanho={24} />
        <span className="adm__etiqueta">plataforma</span>
        <nav className="adm__menu">
          <button className={aba === 'clinicas' ? 'is-ativa' : ''}
                  onClick={() => setAba('clinicas')}>Clínicas</button>
          <button className={aba === 'trilha' ? 'is-ativa' : ''}
                  onClick={() => setAba('trilha')}>Trilha</button>
        </nav>
        <span className="ajuda">{eu.nome}</span>
        <button className="botao botao--fantasma botao--pequeno" onClick={() => void sair()}>
          Sair
        </button>
      </header>

      <main className="adm__corpo">
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        {aba === 'clinicas' && (
          <>
            <div className="adm__numeros">
              <div><span className="adm__rotulo">Clínicas</span>
                   <strong>{clinicas.length}</strong></div>
              <div><span className="adm__rotulo">Pagando</span>
                   <strong>{ativas}</strong></div>
              <div><span className="adm__rotulo">Em teste</span>
                   <strong>{teste}</strong></div>
              <div><span className="adm__rotulo">Receita recorrente</span>
                   <strong>{brl(mrr)}</strong></div>
            </div>

            <input className="entrada adm__busca" type="search"
                   placeholder="Buscar por nome ou endereço"
                   value={busca} onChange={(e) => setBusca(e.target.value)} />

            <div className="rolagem-x">
              <table className="tabela">
                <thead>
                  <tr>
                    <th>Clínica</th><th>Plano</th><th>Situação</th>
                    <th>Uso</th><th>Atividade</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtradas.length === 0 && (
                    <tr><td colSpan={6} className="tabela__vazio">
                      {busca ? 'Nenhuma clínica encontrada.' : 'Nenhuma clínica ainda.'}
                    </td></tr>
                  )}
                  {filtradas.map((c) => {
                    const s = silencio(c.ultima_marcacao)
                    return (
                      <tr key={c.tenant_id}>
                        <td>
                          <strong>{c.nome}</strong>
                          <span className="ajuda"> /{c.slug}</span>
                          <span className="ajuda"> · {c.vertical}</span>
                        </td>
                        <td>
                          {c.plano_nome ?? '—'}
                          <span className="ajuda"> {brl(c.valor_centavos)}</span>
                        </td>
                        <td>
                          <span className={`selo ${
                            c.assinatura === 'ATIVA' ? 'selo--ok'
                            : c.assinatura === 'TESTE' ? 'selo--atencao'
                            : c.assinatura === 'SUSPENSA' ? 'selo--erro' : 'selo--neutro'}`}>
                            {(c.assinatura ?? 'sem assinatura').toLowerCase()}
                          </span>
                        </td>
                        <td className="mono">
                          {c.profissionais ?? 0}
                          {c.max_profissionais ? `/${c.max_profissionais}` : ''} prof
                          <span className="ajuda"> · {c.pacientes ?? 0} pac</span>
                        </td>
                        <td>
                          {/* Silêncio longo é o sinal que antecede o
                              cancelamento — é o que o suporte precisa ver
                              antes de receber o pedido. */}
                          {s ? (
                            <span className={s.dias > 14 ? 'adm__frio' : ''}>
                              {s.texto}
                            </span>
                          ) : <span className="ajuda">nunca marcou</span>}
                          <span className="ajuda"> · {c.marcacoes_30d} em 30d</span>
                        </td>
                        <td>
                          <button className="botao botao--pequeno"
                                  onClick={() => setEditando(c)}>Assinatura</button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {aba === 'trilha' && (
          <>
            <p className="ajuda">
              Tudo que foi feito por aqui, com o motivo. Ninguém apaga — nem o
              administrador, nem o sistema.
            </p>
            <ul className="adm__trilha">
              {trilha.length === 0 && <li className="ajuda">Nada registrado ainda.</li>}
              {trilha.map((r, i) => (
                <li key={i}>
                  <div className="adm__trilha-topo">
                    <strong>{r.acao}</strong>
                    {r.clinica && <span className="ajuda">/{r.clinica}</span>}
                    <span className="ajuda">
                      {new Date(r.ocorrido_em).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <p className="adm__motivo">{r.motivo}</p>
                  <span className="ajuda">{r.admin_email}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </main>

      {editando && (
        <MudarAssinatura
          clinica={editando} planos={planos}
          aoFechar={() => setEditando(null)}
          aoSalvar={() => { setEditando(null); void carregar() }}
        />
      )}
    </div>
  )
}

/* ══ mudar assinatura ═════════════════════════════════════════════ */

function MudarAssinatura({
  clinica, planos, aoFechar, aoSalvar,
}: {
  clinica: Clinica; planos: Plano[]
  aoFechar(): void; aoSalvar(): void
}) {
  const [plano, setPlano] = useState(clinica.plano ?? '')
  const [cortesia, setCortesia] = useState(false)
  const [estado, setEstado] = useState('')
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  const escolhido = planos.find((p) => p.codigo === plano)
  const motivoCurto = motivo.trim().length > 0 && motivo.trim().length < 5

  async function submeter(e: React.FormEvent) {
    e.preventDefault()
    setErro(null); setEnviando(true)
    try {
      await api(`/v1/admin/clinicas/${clinica.tenant_id}/plano`, {
        metodo: 'POST',
        corpo: {
          plano, motivo: motivo.trim(), cortesia,
          ...(estado ? { estado } : {}),
        },
      })
      aoSalvar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível salvar.')
      setEnviando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <aside className="painel" role="dialog" aria-modal="true"
             aria-label={`Assinatura de ${clinica.nome}`}>
        <header className="painel__topo">
          <h2>{clinica.nome}</h2>
          <button className="botao botao--fantasma botao--pequeno" onClick={aoFechar}>
            Fechar
          </button>
        </header>
        <p className="ajuda">
          Hoje: {clinica.plano_nome ?? 'sem plano'} · {brl(clinica.valor_centavos)} ·{' '}
          {(clinica.assinatura ?? '—').toLowerCase()}
        </p>

        <form className="formulario" onSubmit={submeter}>
          {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

          <div className="campo">
            <label htmlFor="m-plano">Plano</label>
            <select id="m-plano" className="entrada" required
                    value={plano} onChange={(e) => setPlano(e.target.value)}>
              <option value="">Escolha…</option>
              {planos.filter((p) => p.ativo).map((p) => (
                <option key={p.codigo} value={p.codigo}>
                  {p.nome} — {brl(p.preco_centavos)}
                </option>
              ))}
            </select>
          </div>

          <label className="adm__caixa">
            <input type="checkbox" checked={cortesia}
                   onChange={(e) => setCortesia(e.target.checked)} />
            <span>
              <strong>Cortesia</strong>
              {/* Zerar o valor mas manter o plano registrado: a clínica
                  vê o que vai pagar quando a cortesia acabar, em vez de
                  descobrir na primeira fatura. */}
              <span className="ajuda">
                Zera o valor e mantém o plano registrado. É o caso do piloto.
              </span>
            </span>
          </label>

          {escolhido && (
            <div className="aviso aviso--ok">
              Vai ficar: <strong>{escolhido.nome}</strong> ·{' '}
              {cortesia ? 'R$ 0,00 (cortesia)' : brl(escolhido.preco_centavos)}
            </div>
          )}

          <div className="campo">
            <label htmlFor="m-estado">Situação (opcional)</label>
            <select id="m-estado" className="entrada"
                    value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="">Manter como está</option>
              <option value="ATIVA">Ativa</option>
              <option value="TESTE">Em teste</option>
              <option value="INADIMPLENTE">Inadimplente</option>
              <option value="SUSPENSA">Suspensa</option>
            </select>
          </div>

          <div className="campo">
            <label htmlFor="m-motivo">Motivo</label>
            <textarea id="m-motivo" className="entrada" rows={2} required
                      placeholder="Ex.: piloto — liberado para avaliação até 30/09"
                      aria-invalid={motivoCurto}
                      value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            <span className={motivoCurto ? 'ajuda ajuda--erro' : 'ajuda'}>
              {motivoCurto
                ? 'Escreva um motivo de verdade, com pelo menos 5 letras.'
                : 'Obrigatório. Fica na trilha para sempre, e é o que responde “por que mexeram nisso?”.'}
            </span>
          </div>

          <button className="botao" type="submit"
                  disabled={enviando || !plano || motivo.trim().length < 5}>
            {enviando ? 'Salvando…' : 'Aplicar'}
          </button>
        </form>
      </aside>
    </>
  )
}
